import {useRequiredConfig} from '@domain/shifthandover'
import {
  changeCustomTaskRepetition,
  getDefaultWeekdays
} from '@hconnect/common/components/repetitiveTask'
import {TaskRepetitionInterval} from '@hconnect/common/components/repetitiveTask/TaskRepetitionInterval'
import {TaskRepetitionLabel} from '@hconnect/common/components/shiftEventDetails/TaskRepetitionLabel'
import {CustomTaskRepetitionDropDown} from '@hconnect/common/components/shiftEventFormFields'
import {CustomTaskRepetition, isCustomWeeklyRepetition} from '@hconnect/common/types'
import {GridSetting} from '@hconnect/uikit'
import {WeekDaySelect} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Close'
import {Button, Dialog, DialogContent, DialogTitle, DialogActions, Grid, Box} from '@mui/material'
import {isArray} from 'lodash'
import moment from 'moment-timezone'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {ShiftPicker} from '../dateTimeAndShift/ShiftPicker'

import {TaskRepetitionEndingOptions} from './TaskRepetitionEndingOptions'

type Props = {
  plantId: string
  customTaskRepetition: CustomTaskRepetition
  onClose: () => void
  onChange: (next: CustomTaskRepetition) => void
}

type CustomRecurringTaskProps = {
  plantId: string
  settings: CustomTaskRepetition
  onChange: (next: CustomTaskRepetition) => void
}

const regularSize: GridSetting = {xs: 12}

const CustomRecurringTask: React.FC<CustomRecurringTaskProps> = ({plantId, settings, onChange}) => {
  const timezone = useUpmPlantTimezone(plantId)
  const {shifts} = useRequiredConfig(plantId)
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)

  return (
    <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
      <Grid item {...regularSize}>
        <ShiftPicker
          plantId={plantId}
          shifts={shifts}
          label={t('recurringDialog.startsOn')}
          localDate={settings.startDate}
          onChange={(particularShift) => {
            if (isArray(particularShift)) return
            const next = {
              ...settings,
              startDate: moment.tz(particularShift.endDate, timezone)
            }

            if (isCustomWeeklyRepetition(next) && next.weekDays.length === 1) {
              next.weekDays = getDefaultWeekdays(next.startDate)
            }

            onChange(next)
          }}
        />
      </Grid>
      <Grid item {...regularSize}>
        <CustomTaskRepetitionDropDown
          translate={t}
          data-test-id="task-custom-repetition-unit"
          onChange={(nextRepetition) => {
            if (nextRepetition === settings.repetition) return

            const next = changeCustomTaskRepetition(nextRepetition, settings)
            onChange(next)
          }}
          // Note: Monthly and yearly will supported in future
          value={settings.repetition as 'daily' | 'weekly'}
          required
        />
      </Grid>
      <Grid
        item
        {...regularSize}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <Box component={'span'} width={50}>
          {t('recurringDialog.interval')}
        </Box>
        <TaskRepetitionInterval
          t={t}
          sx={{
            width: 75,
            '&.MuiFormControl-root': {
              mr: 1
            }
          }}
          settings={settings}
          onChange={(nextInterval) => onChange({...settings, repeatEvery: nextInterval})}
        />
      </Grid>

      {isCustomWeeklyRepetition(settings) && (
        <Grid
          item
          {...regularSize}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <Box component={'span'} width={50}>
            {t('recurringDialog.onWeekday')}
          </Box>
          <WeekDaySelect
            selectedDays={settings.weekDays}
            onChange={(weekDays) => {
              if (weekDays.length > 0) onChange({...settings, weekDays})
            }}
          />
        </Grid>
      )}

      <Grid
        item
        {...regularSize}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}
      >
        <Box component={'span'} width={50}>
          {t('recurringDialog.ends')}
        </Box>
        <TaskRepetitionEndingOptions plantId={plantId} onChange={onChange} settings={settings} />
      </Grid>

      {settings.repetition && (
        <Grid item {...regularSize}>
          <TaskRepetitionLabel
            settings={settings}
            omitFirstOccurrence
            plantId={plantId}
            timezone={timezone}
          />
        </Grid>
      )}
    </Grid>
  )
}

export const TaskRepetitionDialog: React.FC<Props> = (props) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const [settings, updateCustomTaskRepetition] = useState<CustomTaskRepetition>(
    props.customTaskRepetition
  )

  return (
    <Dialog maxWidth="xs" data-test-id="task-custom-repetition-modal" onClose={props.onClose} open>
      <DialogTitle>{t('recurringDialog.title')}</DialogTitle>
      <DialogContent>
        <CustomRecurringTask
          plantId={props.plantId}
          settings={settings}
          onChange={updateCustomTaskRepetition}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} startIcon={<CancelIcon />}>
          {t('action.cancel')}
        </Button>
        <Button onClick={() => props.onChange(settings)} color="primary" startIcon={<CheckIcon />}>
          {t('shiftEvent.action.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
